import { Checkbox, CircularProgress, Dialog, TextField, Tooltip } from '@material-ui/core'
import { Autocomplete } from '@material-ui/lab'
import { Button } from 'components/facets'
import { ArticleAttributes } from 'components/facets/ArticleAttributes'
import ImagePopover from 'components/facets/ImagePopover'
import { useUser } from 'components/providers/UserProvider'
import { confirmGradeRelabel, getGradeRelabelRows, GradeRelabelRow } from 'lib/api'
import { getGameCodeFromName, getPriceFormat } from 'lib/utils'
import _ from 'lodash'
import { useSnackbar } from 'notistack'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { AutoSizer, Column, Table } from 'react-virtualized'
import 'react-virtualized/styles.css'
import { getGameFromId } from 'shared'
import { getProductImageUrl } from 'utils/imageUtils'

// undefined: the seller still has to answer. null: the card is not graded after all.
type Choice = string | null | undefined

// The grade the card is almost certainly moving to: the one its company awards today carrying the number the
// card is leaving ("10.0 Gem-Mint" for a PSA 10). Two of them, as BGS has, or a grade with no number to read,
// as Unknown, leave the question open. It fills the row in and the seller can still pick another.
const suggested = (row: GradeRelabelRow): string | undefined => {
  const number = row.ptProduct.grade.match(/^\d+(\.\d)?/)?.[0]
  if (number === undefined) return undefined

  // "10" is the same number as "10.0": stock older than the grading table still carries it without its decimal.
  const normalized = number.includes('.') ? number : `${number}.0`
  const sameNumber = row.candidates.filter(
    (grade) => grade === normalized || grade.startsWith(`${normalized} `)
  )

  return sameNumber.length === 1 ? sameNumber[0] : undefined
}

// Rails says why it refused a row (a grade it did not offer, an article that is not theirs); that reason is what
// tells a bug from a stale dialog, so it goes into the message instead of dying in the catch.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const withDetail = (message: string, error: any): string => {
  const detail = error?.response?.data?.error?.message ?? error?.response?.data?.error
  return typeof detail === 'string' ? `${message}: ${detail}` : message
}

export const GradeRelabelDialog = ({ onClose }: { onClose: () => void }): JSX.Element => {
  const { t } = useTranslation()
  const { enqueueSnackbar } = useSnackbar()
  const { user, setUser } = useUser()
  const [rows, setRows] = useState<GradeRelabelRow[] | undefined>()
  const [completedAt, setCompletedAt] = useState<string | null>(null)
  const [choices, setChoices] = useState<Record<number, Choice>>({})
  const [saving, setSaving] = useState(false)

  // The date and the count are Rails' to write — when it queues the job, and when it answers a seller who has
  // nothing to relabel — so this only keeps the user this page holds in step with them. Both, since the sync sends
  // the whole ptInventory back from this copy and would drop whatever is missing from it.
  const rememberCompleted = (cardsToFix: number) =>
    setUser({
      ...user,
      ptInventory: {
        ...user.ptInventory,
        gradeRelabelCompletedAt: new Date(),
        gradeRelabelCardsToFix: cardsToFix,
      },
    })

  useEffect(() => {
    getGradeRelabelRows()
      .then(({ rows: fetched, completedAt: done }) => {
        setCompletedAt(done)
        // A row that suggests itself is already answered, so only the others wait for the seller. The order is
        // fixed here, once: sorting on every render would move a row away from under the seller's cursor.
        const known = (row: GradeRelabelRow) => suggested(row) !== undefined
        setRows([...fetched.filter((row) => !known(row)), ...fetched.filter(known)])
        setChoices(_.fromPairs(fetched.map((row) => [row.ptArticle._id, suggested(row)])))
        if (fetched.length === 0) rememberCompleted(0)
      })
      .catch((error) => {
        console.error(error)
        enqueueSnackbar(withDetail(t('gradeRelabel.loadFailed'), error), { variant: 'error' })
        onClose()
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const unanswered = rows?.filter((row) => choices[row.ptArticle._id] === undefined).length ?? 0

  const confirm = async () => {
    if (rows === undefined) return

    setSaving(true)
    try {
      await confirmGradeRelabel(
        rows.map((row) => ({
          ptArticleId: row.ptArticle._id,
          grade: choices[row.ptArticle._id] ?? null,
        }))
      )
      rememberCompleted(rows.length)
      enqueueSnackbar(t('gradeRelabel.saved'), { variant: 'success' })
      onClose()
    } catch (error) {
      console.error(error)
      enqueueSnackbar(withDetail(t('gradeRelabel.saveFailed'), error), { variant: 'error' })
    } finally {
      setSaving(false)
    }
  }

  const setChoice = (ptArticleId: number, choice: Choice) =>
    setChoices((current) => ({ ...current, [ptArticleId]: choice }))

  return (
    // Small until there is a table to hold: it opens at the size of the message, and widens only for the
    // seller who has rows, instead of shrinking under everybody else's eyes.
    <Dialog
      open
      maxWidth={rows?.length ? 'lg' : 'sm'}
      fullWidth
      onClose={onClose}
      disableBackdropClick
    >
      <div className="p-4">
        <h3 className="mb-3">{t('gradeRelabel.title')}</h3>
        {rows === undefined ? (
          <div className="d-flex justify-content-center p-5">
            <CircularProgress />
          </div>
        ) : rows.length === 0 ? (
          <p>
            {completedAt === null
              ? t('gradeRelabel.nothingToFix')
              : t('gradeRelabel.alreadyDone', { date: new Date(completedAt).toLocaleDateString() })}
          </p>
        ) : (
          <>
            <p>{t('gradeRelabel.intro')}</p>
            {/* A stock can hold hundreds of graded cards, so the rows are virtualized like every other
                article list in the application. */}
            <div style={{ height: '60vh' }}>
              <AutoSizer>
                {({ height, width }) => (
                  <Table
                    width={width}
                    height={height}
                    headerHeight={40}
                    rowHeight={64}
                    rowCount={rows.length}
                    rowGetter={({ index }) => rows[index]}
                  >
                    <Column
                      width={55}
                      flexGrow={0}
                      flexShrink={0}
                      label=""
                      dataKey=""
                      cellRenderer={({ rowData }: { rowData: GradeRelabelRow }) => (
                        <Tooltip title={getGameFromId(rowData.ptProduct.idGame) || ''}>
                          <span
                            className={`ss-${getGameCodeFromName(
                              getGameFromId(rowData.ptProduct.idGame)
                            )} ss-game-icon ss-fw`}
                            style={{ fontSize: '1.5rem' }}
                          />
                        </Tooltip>
                      )}
                    />
                    <Column
                      width={60}
                      flexGrow={0}
                      flexShrink={0}
                      label=""
                      dataKey=""
                      cellRenderer={({ rowData }: { rowData: GradeRelabelRow }) => (
                        <ImagePopover
                          variant="iconPopover"
                          imageComponent={
                            <img
                              width="62px"
                              src={getProductImageUrl(rowData.ptProduct.mkmProductId.toString())}
                            />
                          }
                          imageId={rowData.ptProduct.mkmProductId.toString()}
                        />
                      )}
                    />
                    <Column
                      width={200}
                      flexGrow={2}
                      flexShrink={1}
                      label={t('card.card')}
                      dataKey=""
                      cellRenderer={({ rowData }: { rowData: GradeRelabelRow }) => (
                        <div className="d-flex flex-column">
                          <span>
                            {rowData.quantity}x {rowData.ptProduct.name}
                          </span>
                          <small>
                            {rowData.ptProduct.expansionCode} - {rowData.ptProduct.cn}
                          </small>
                        </div>
                      )}
                    />
                    <Column
                      width={160}
                      flexGrow={1}
                      flexShrink={1}
                      label=""
                      dataKey=""
                      cellRenderer={({ rowData, rowIndex }) => (
                        <ArticleAttributes
                          article={{ ...rowData.ptProduct, ptProductId: rowData.ptProduct._id }}
                          identifierForDataTestId={`relabel-${rowIndex}`}
                          iconHeight={17}
                        />
                      )}
                    />
                    <Column
                      width={90}
                      flexGrow={0}
                      flexShrink={0}
                      label={t('common.price')}
                      dataKey=""
                      cellRenderer={({ rowData }: { rowData: GradeRelabelRow }) => (
                        <span>
                          {getPriceFormat(rowData.ptArticle.baseSellPrice, user.currency)}
                        </span>
                      )}
                    />
                    <Column
                      width={160}
                      flexGrow={2}
                      flexShrink={1}
                      label={t('common.comment')}
                      dataKey=""
                      cellRenderer={({ rowData }: { rowData: GradeRelabelRow }) => (
                        <span>{rowData.ptArticle.comment}</span>
                      )}
                    />
                    <Column
                      width={160}
                      flexGrow={2}
                      flexShrink={1}
                      label={t('common.location')}
                      dataKey=""
                      cellRenderer={({ rowData }: { rowData: GradeRelabelRow }) => (
                        <span>
                          {_.uniq(
                            rowData.ptArticle.locations.map((location) => location.name)
                          ).join(', ')}
                        </span>
                      )}
                    />
                    <Column
                      width={200}
                      flexGrow={1}
                      flexShrink={0}
                      label={t('gradeRelabel.newGrade')}
                      dataKey=""
                      cellRenderer={({ rowData, rowIndex }) => {
                        const id = rowData.ptArticle._id
                        const choice = choices[id]
                        return (
                          <Autocomplete
                            className="w-100"
                            options={[...rowData.candidates].reverse()}
                            value={choice ?? null}
                            disabled={choice === null}
                            autoHighlight
                            onChange={(_event, value) => setChoice(id, value ?? undefined)}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                placeholder={t('inventory.insertGradeValue')}
                                inputProps={{
                                  ...params.inputProps,
                                  'data-testid': `relabel-grade-${rowIndex}`,
                                }}
                              />
                            )}
                          />
                        )
                      }}
                    />
                    <Column
                      width={90}
                      flexGrow={0}
                      flexShrink={0}
                      label={t('stockPricing.isGraded')}
                      dataKey=""
                      cellRenderer={({ rowData, rowIndex }) => {
                        const row = rowData as GradeRelabelRow
                        const choice = choices[row.ptArticle._id]
                        return (
                          <Checkbox
                            data-testid={`relabel-is-graded-${rowIndex}`}
                            checked={choice !== null}
                            onChange={(event) =>
                              setChoice(
                                row.ptArticle._id,
                                event.target.checked ? suggested(row) : null
                              )
                            }
                          />
                        )
                      }}
                    />
                  </Table>
                )}
              </AutoSizer>
            </div>
          </>
        )}
        <div className="d-flex justify-content-end gap-2 mt-4">
          <Button variant="outlined" onClick={onClose}>
            {t('common.close')}
          </Button>
          {rows !== undefined && rows.length > 0 && (
            <Button
              variant="contained"
              data-testid="relabel-confirm"
              disabled={unanswered > 0 || saving}
              onClick={confirm}
            >
              {unanswered > 0
                ? t('gradeRelabel.stillToAnswer', { count: unanswered })
                : t('gradeRelabel.confirm')}
            </Button>
          )}
        </div>
      </div>
    </Dialog>
  )
}
